import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchConversations,
  type MessagingConversation,
} from '../api/messagingApi';
import { AssetRequestError } from '../api/getAssetById';
import { useAuth } from '../auth/AuthContext';
import { sortMessagingInbox, sumMessagingUnread } from './messagingInboxSort';

export { sortMessagingInbox, sumMessagingUnread } from './messagingInboxSort';

const INBOX_POLL_MS = 8000;

type MessagingInboxContextValue = {
  conversations: MessagingConversation[];
  unreadTotal: number;
  loading: boolean;
  error: string | null;
  refresh: (opts?: { soft?: boolean }) => Promise<void>;
  setConversations: (
    update:
      | MessagingConversation[]
      | ((prev: MessagingConversation[]) => MessagingConversation[]),
  ) => void;
  markConversationUnreadZero: (conversationId: string) => void;
};

const MessagingInboxContext = createContext<MessagingInboxContextValue | null>(null);

export function MessagingInboxProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, isAuthReady } = useAuth();
  const [conversations, setConversations] = useState<MessagingConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(
    async (opts?: { soft?: boolean }) => {
      if (!isLoggedIn) {
        setConversations([]);
        setError(null);
        setLoading(false);
        return;
      }
      if (!opts?.soft) {
        setLoading(true);
      }
      try {
        const rows = await fetchConversations();
        setConversations(sortMessagingInbox(rows));
        setError(null);
      } catch (err) {
        const message =
          err instanceof AssetRequestError ? err.message : 'messaging-inbox-failed';
        setError(message);
      } finally {
        if (!opts?.soft) {
          setLoading(false);
        }
      }
    },
    [isLoggedIn],
  );

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }
    if (!isLoggedIn) {
      setConversations([]);
      setError(null);
      setLoading(false);
      return;
    }
    void refresh();
    const timer = window.setInterval(() => {
      void refresh({ soft: true });
    }, INBOX_POLL_MS);
    return () => window.clearInterval(timer);
  }, [isAuthReady, isLoggedIn, refresh]);

  const markConversationUnreadZero = useCallback((conversationId: string) => {
    setConversations((prev) =>
      sortMessagingInbox(
        prev.map((row) =>
          row.id === conversationId ? { ...row, unreadCount: 0 } : row,
        ),
      ),
    );
  }, []);

  const unreadTotal = useMemo(() => sumMessagingUnread(conversations), [conversations]);

  const value = useMemo<MessagingInboxContextValue>(
    () => ({
      conversations,
      unreadTotal,
      loading,
      error,
      refresh,
      setConversations: (update) => {
        setConversations((prev) => {
          const next = typeof update === 'function' ? update(prev) : update;
          return sortMessagingInbox(next);
        });
      },
      markConversationUnreadZero,
    }),
    [conversations, unreadTotal, loading, error, refresh, markConversationUnreadZero],
  );

  return (
    <MessagingInboxContext.Provider value={value}>{children}</MessagingInboxContext.Provider>
  );
}

export function useMessagingInbox(): MessagingInboxContextValue {
  const value = useContext(MessagingInboxContext);
  if (!value) {
    throw new Error('useMessagingInbox must be used within MessagingInboxProvider');
  }
  return value;
}
